import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Buildings, Grid4, LogoutCurve, Profile2User } from "iconsax-react";
import { AccessibilitySwitcher, LanguageSwitcher, toggleSidebar, selectSidebarCollapsed } from "@/features/settings";
import { useLogoutMutation } from "@/features/auth/api";
import { setLoggingOut } from "@/features/auth/slice";
import { setActiveProject } from "@/features/settings";
import { runtimeConfig } from "@/shared/config/runtime";
import { useAppDispatch, useAppSelector, useAuth, useBreadcrumbs } from "@/shared/hooks";
import { LOGIN_PAGE_ROUTE, ORGANIZATION_PAGE_ROUTE, PROFILE_PAGE_ROUTE, clearFormDrafts } from "@/shared/utils";
import { Avatar, Breadcrumbs, Button, Dropdown, DropdownItem } from "@/shared/ui";
import { NotificationBell } from "@/features/notifications";
import { DemoBadge, DemoDataControls } from "@/features/demo";

export default function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs();
  const user = useAppSelector((state) => state.auth.data?.user ?? null);
  const collapsed = useAppSelector(selectSidebarCollapsed);
  const { hasRole } = useAuth();

  const { t } = useTranslation("Sidebar");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isLoggingOut = useAppSelector((state) => state.auth.isLoggingOut);
  const [logoutRequest, { isLoading: isLogoutRequestLoading }] = useLogoutMutation();
  const isLogoutLoading = isLogoutRequestLoading || isLoggingOut;

  async function handleLogout() {
    if (isLogoutLoading) return;
    dispatch(setActiveProject(null));
    dispatch(setLoggingOut(true));
    try {
      await logoutRequest().unwrap();
    } catch {
      void 0;
    } finally {
      clearFormDrafts();
      window.location.href = LOGIN_PAGE_ROUTE;
    }
  }

  return (
    <header className="sticky top-0 z-[100] surface-secondary-fill w-full p-5 pb-4 pl-0 flex justify-between items-center">
      <div className="flex items-center gap-3">
        <Button
          variant={collapsed ? "primary" : "secondary"}
          className="w-10 h-10 flex justify-center items-center p-1! rounded-md!"
          onClick={() => dispatch(toggleSidebar())}>
          <span className={collapsed ? "" : "content-base-secondary"}>
            <Grid4 size={20} color="currentColor" />
          </span>
        </Button>
        {breadcrumbs.length > 0 && (
          <>
            <span className="w-px h-6 bg-alpha-black-10" />
            <Breadcrumbs items={breadcrumbs} />
          </>
        )}
        {runtimeConfig.isDemo && (
          <>
            <span className="w-px h-6 bg-alpha-black-10" />
            <DemoBadge />
          </>
        )}
      </div>

      <div className="flex justify-end items-center gap-3 relative">
        {runtimeConfig.isDemo && <DemoDataControls />}
        <NotificationBell />
        <LanguageSwitcher />
        <AccessibilitySwitcher />
        <Dropdown open={dropdownOpen} onClose={() => setDropdownOpen(false)} align="right">
          <button type="button" className="cursor-pointer" onClick={() => setDropdownOpen((prev) => !prev)}>
            <Avatar
              name={user?.user_type === "msb_contractor" ? (user.organization?.name ?? "") : (user?.profile?.full_name ?? "")}
              size={40}
              textClassName="text-sm"
            />
          </button>
          <DropdownItem
            onClick={() => {
              setDropdownOpen(false);
              navigate(PROFILE_PAGE_ROUTE);
            }}>
            <Profile2User size={18} color="currentColor" className="shrink-0" />
            <span className="text-label-sm">{t("myProfile")}</span>
          </DropdownItem>
          {hasRole("chief") && (
            <DropdownItem
              onClick={() => {
                setDropdownOpen(false);
                navigate(ORGANIZATION_PAGE_ROUTE);
              }}>
              <Buildings size={18} color="currentColor" className="shrink-0" />
              <span className="text-label-sm">{t("myOrganization")}</span>
            </DropdownItem>
          )}
          <DropdownItem onClick={handleLogout} disabled={isLogoutLoading}>
            <LogoutCurve size={18} color="currentColor" className="content-action-negative shrink-0" />
            <span className="text-label-sm content-action-negative">
              {isLogoutLoading ? t("logout.loading") : t("logout")}
            </span>
          </DropdownItem>
        </Dropdown>
      </div>
    </header>
  );
}
